import { spawnSync, execSync } from "node:child_process";
import fs from "node:fs/promises";
import path from "node:path";

/**
 * Determines which npm scripts are available in the consumer project.
 */
async function readScripts(cwd: string): Promise<Record<string, string>> {
  try {
    const pkg = JSON.parse(await fs.readFile(path.join(cwd, "package.json"), "utf8"));
    return pkg.scripts || {};
  } catch {
    return {};
  }
}

/**
 * Returns the list of files changed since the last commit.
 * Used to detect whether remediation produced any observable change.
 */
function getChangedFiles(cwd: string): string[] {
  try {
    const output = execSync("git status --short", { cwd, encoding: "utf8" });
    return output
      .split("\n")
      .filter(Boolean)
      .map((line) => line.slice(3).trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * Maps an affectedCheck source string to likely npm script names to re-run.
 */
function resolveRetryScripts(affectedChecks: string[], scripts: Record<string, string>): string[] {
  const retry = new Set<string>();

  for (const check of affectedChecks) {
    // Evidence failures → re-run the failing validation scripts
    if (check === "evidence" || check === "result") {
      if (scripts.test) retry.add("test");
      if (scripts.lint) retry.add("lint");
      if (scripts.build) retry.add("build");
      if (scripts.typecheck) retry.add("typecheck");
    }
    // Specific check types
    if (check.includes("test")) retry.add("test");
    if (check.includes("lint")) retry.add("lint");
    if (check.includes("build")) retry.add("build");
    if (check.includes("typecheck")) retry.add("typecheck");
  }

  // Filter to only scripts that actually exist
  return [...retry].filter((name) => !!scripts[name]);
}

export interface RemediationExecutorContext {
  attempt: number;
  maxAttempts: number;
  mode: string;
  taskSlug: string;
  result: any;
  affectedChecks: string[];
  requestPath: string;
}

export interface RemediationExecutorResult {
  applied: boolean;
  changedFiles?: string[];
  evidenceAdded?: string[];
  reason?: string;
}

/**
 * CLI Remediation Executor — default implementation for `ai-workflow run`.
 */
export function createCliRemediationExecutor(cwd: string) {
  return async function remediationExecutor({
    attempt,
    maxAttempts,
    mode,
    affectedChecks,
    requestPath
  }: RemediationExecutorContext): Promise<RemediationExecutorResult> {
    console.log(`\n[HEALER] Attempt ${attempt}/${maxAttempts} — mode: ${mode}`);
    console.log(`[HEALER] Affected: ${affectedChecks.join(", ")}`);

    const scripts = await readScripts(cwd);
    const filesBefore = getChangedFiles(cwd);
    const toRetry = resolveRetryScripts(affectedChecks, scripts);

    if (toRetry.length === 0) {
      console.log("[HEALER] No retryable scripts found for the affected checks.");
      console.log(`[HEALER] Review ${requestPath} for details.`);
      return {
        applied: false,
        reason: `No npm scripts available to address: ${affectedChecks.join(", ")}. Manual remediation required.`
      };
    }

    console.log(`[HEALER] Re-running: ${toRetry.map((s) => `npm run ${s}`).join(", ")}`);

    const results: any[] = [];
    let anyPassed = false;

    for (const scriptName of toRetry) {
      process.stdout.write(`  → npm run ${scriptName} ... `);
      const isWin32 = process.platform === "win32";
      const cmd = isWin32 ? "npm.cmd" : "npm";
      const result = spawnSync(cmd, ["run", scriptName], {
        cwd,
        shell: false,
        encoding: "utf8",
        timeout: 120000
      });
      const passed = result.status === 0;
      if (passed) anyPassed = true;
      console.log(passed ? "PASS" : "FAIL");
      results.push({ script: scriptName, passed, exitCode: result.status });
    }

    const filesAfter = getChangedFiles(cwd);
    const changedFiles = filesAfter.filter((f) => !filesBefore.includes(f));

    if (!anyPassed && changedFiles.length === 0) {
      console.log("[HEALER] No scripts passed and no files changed — remediation had no effect.");
      return {
        applied: false,
        reason: `Re-ran ${toRetry.join(", ")} — all failed. Structural fix required by the implementing agent.`
      };
    }

    console.log(`[HEALER] Applied: ${results.filter((r) => r.passed).map((r) => r.script).join(", ") || "none passed"}`);
    if (changedFiles.length) console.log(`[HEALER] Changed files: ${changedFiles.join(", ")}`);

    return {
      applied: true,
      changedFiles,
      evidenceAdded: anyPassed ? ["validation-output"] : []
    };
  };
}
